// Copyright (c) 2026 Michael F. Kamprath
//
// English UI string dictionary.  Flat-keyed object so the Phase 3
// i18n system can pick strings by locale without a nested-path walk.
// Keys are namespaced by UI area (`settings.*`, `home.*`, ...) so
// future locales can ship partial coverage without collision.
//
// When adding a key, also add it to any other locale dictionaries
// (none shipped yet).  Untranslated keys fall back to English via
// the ./index.js resolver.

export default {
  // --- Splash overlay -----------------------------------------------------
  // Full-screen tap-to-start overlay shown on first load (gates audio
  // unlock).  Title stays "Ewe Can Count" (brand) in every locale; only
  // the button label localizes.  See src/ui/splash.js.
  'splash.tapToStart':                 'Tap to start',

  // --- Orientation prompt -------------------------------------------------
  // Shown as a full-screen overlay when the viewport aspect ratio drops
  // below ~1:1.  Two variants based on input device: phone (rotate) vs.
  // desktop (resize the window).  See src/ui/orientation-prompt.js.
  'orientationPrompt.rotateToLandscape': 'Please rotate your device to landscape',
  'orientationPrompt.widenBrowser':      'Please widen your browser window',

  // --- Home screen --------------------------------------------------------
  'home.title':                        'Ewe Can Count',
  'home.button.learn':                 'Learn',
  'home.button.quiz':                  'Quiz',
  'home.button.settings':              'Settings',

  // --- Settings panel: top-level chrome -----------------------------------
  'settings.title':                    'Settings',
  'settings.close':                    'Close',

  // --- Settings panel: section headings -----------------------------------
  'settings.section.gameplay':         'Gameplay',
  'settings.section.language':         'Language',
  'settings.section.themes':           'Themes',
  'settings.section.support':          'Support',
  'settings.section.family':           'More Ewe Apps',
  'settings.section.privacy':          'Privacy',
  'settings.section.credits':          'Credits',
  'settings.section.about':            'About',

  // --- Gameplay section ---------------------------------------------------
  'settings.maxCount':                       'Max count',
  'settings.entranceAnimations':             'Entrance animations',
  'settings.verbalPrompts':                  'Verbal prompts',
  'settings.verbalPromptMode.all':           'All prompts',
  'settings.verbalPromptMode.counting-only': 'Counting only',
  'settings.verbalPromptMode.off':           'Off',
  'settings.fullscreen':                     'Fullscreen',
  'settings.fullscreen.iosHint':
    'To use Ewe Can Count full-screen on iPhone or iPad, open Safari\u2019s Share menu and choose \u201cAdd to Home Screen.\u201d Launching from the home-screen icon hides the browser chrome.',

  // --- Language section ---------------------------------------------------
  'settings.language':                 'Language',
  'settings.language.locale.en':       'English',
  'settings.language.locale.zh-CN':    '\u7b80\u4f53\u4e2d\u6587',   // 简体中文
  'settings.language.locale.ja':       '\u65e5\u672c\u8a9e',          // 日本語
  'settings.language.locale.ko':       '\ud55c\uad6d\uc5b4',          // 한국어
  'settings.numeralScript':            'Numerals',
  'settings.numeralScript.arabic':     'Arabic',
  'settings.numeralScript.chinese':    'Chinese',
  'settings.numeralScript.japanese':   'Japanese',
  'settings.numeralScript.sino-korean':  'Sino-Korean',
  'settings.numeralScript.native-korean': 'Native Korean',

  // --- Themes section -----------------------------------------------------
  'settings.themes.hint':              'Tap a theme to turn it on or off.  At least one theme must be active.',
  'settings.themes.minOneTooltip':     'At least one theme must be active',
  'settings.theme.sheep':              'Sheep',
  'settings.theme.cows':               'Cows',
  'settings.theme.horses':             'Horses',
  'settings.theme.chicks':             'Chicks',
  'settings.theme.apples':             'Apples',
  'settings.theme.flowers':            'Flowers',

  // --- Support section ----------------------------------------------------
  'settings.support.heading':          'Support Ewe Can Count',
  // Wording deliberately uses "support" rather than "donate"/"donation":
  // it sets a friendlier, lower-pressure tone for parents and matches
  // the language used elsewhere in the section (heading, button, ask).
  'settings.support.description':
    'Ewe Can Count is free and ad-free. If you enjoy it, a little support helps cover hosting and development.',
  // Mirrors the I Hear Ewe wording so the two sibling apps use a
  // consistent ask -- "Support <appName> with $2".  The "$2" is a
  // literal suggested donation amount, not a placeholder.
  'settings.support.donate':           'Support Ewe Can Count with $2',
  'settings.support.offline':          'Connect to the internet to send your support.',
  'settings.support.poweredBy':        'Powered by PayPal',

  // --- Family cross-promo section -----------------------------------------
  'settings.family.body':              'Other apps in the Ewe family, made for kids:',
  'settings.family.offline':           'Connect to the internet to explore more Ewe apps.',
  'family.iHearEwe.tagline':           'Tap to hear animals and everyday sounds.',
  'family.eweCanCount.tagline':        'Count along with friendly farm animals.',

  // --- Privacy section ----------------------------------------------------
  'settings.privacy.shareStats':       'Share anonymous usage stats',
  'settings.privacy.disclosurePrefix':
    'No personal data is collected \u2014 all progress stays on your device.  If this toggle is on, anonymous aggregate counts (e.g., \u201ca correct answer was given\u201d) are sent to help improve the app.  ',
  'settings.privacy.viewStats':        'View the stats page \u2192',

  // --- Credits section (static multi-line content) ------------------------
  'settings.credits.art.heading':      'Art',
  'settings.credits.art.name':         'Verne Lindner Creative Services',

  'settings.credits.voices.en.heading':   'Voices \u2014 English',
  'settings.credits.voices.en.people':    'Michael Kamprath / Gloria Tan / Madeline Kamprath',

  'settings.credits.voices.zh.heading':   'Voices & Translations \u2014 \u4e2d\u6587 (Simplified Chinese)',
  'settings.credits.voices.zh.people':    'Iris Wang',

  'settings.credits.voices.ja.heading':   'Voices & Translations \u2014 \u65e5\u672c\u8a9e (Japanese)',
  'settings.credits.voices.ja.people':    'Shino Funato',

  'settings.credits.voices.ko.heading':   'Voices & Translations \u2014 \ud55c\uad6d\uc5b4 (Korean)',
  'settings.credits.voices.ko.people':    'Kahye Song',

  'settings.credits.music.heading':       'Music',
  // Each entry renders as `"Title"  \u00a9 Publisher` (two-space gutter).
  'settings.credits.music.1.title':       'Frailing Banjo',
  'settings.credits.music.1.publisher':   'Chestnut Mills Musicraft',
  'settings.credits.music.2.title':       'Apple on the Ground',
  'settings.credits.music.2.publisher':   'Suicide Jacks',
  'settings.credits.music.3.title':       'Island Romp',
  'settings.credits.music.3.publisher':   'Cliffdweller Music',
  'settings.credits.music.4.title':       'Coconut Island',
  'settings.credits.music.4.publisher':   'Fireclown Publishing',
  'settings.credits.music.5.title':       'Flight of the Bumble Bee',
  'settings.credits.music.5.publisher':   'Westar Music Publishing',

  // --- About section ------------------------------------------------------
  'settings.about.appName':            'Ewe Can Count',
  'settings.about.copyright':          '\u00a9 2011\u20132026 Michael F. Kamprath',
  'settings.about.contact':            'info@claireware.com',
  'settings.about.privacyHeading':     'Privacy',
  'settings.about.privacyParagraph1':
    'Ewe Can Count does not collect any personal information \u2014 no accounts, no identifiers, no tracking, no ads.  Everything the app needs to work runs on this device.',
  'settings.about.privacyParagraph2Prefix':
    'The one exception: Ewe Can Count reports anonymous, aggregate counts of which numbers and themes have been practiced, so I know which parts of the app kids use most.  No personal information is ever attached to these counts, and you can turn this off in Privacy settings above.  You can see the numbers yourself on ',
  'settings.about.privacyStatsLinkText': 'the Ewe Can Count stats page',
  'settings.about.privacyParagraph2Suffix': '.',
  'settings.about.versionLabel':       'Version',
};
